import { isTestEnv } from '@functions/functions';
import Address from '@models/Address';
import { atom } from 'jotai';
import { atomFamily, selectAtom } from 'jotai/utils';
import { EWalletId, EWalletType } from './web3Store.enums';
import { addWeb3Account, updateWeb3Account } from './web3Store.functions';
import { TProviderMap, TWeb3Account } from './web3Store.type';
import { NETWORKS_DATA, VALID_CHAIN_IDS, WALLETS } from './web3Store.variables';

export const web3AccountsAtom = atom<TWeb3Account[]>([]);

export const addWeb3AccountsAtom = atom(null, (get, set, newWeb3Account: TWeb3Account) =>
	set(web3AccountsAtom, addWeb3Account(get(web3AccountsAtom), newWeb3Account))
);

export const removeWeb3AccountAtom = atom(null, (get, set, id: EWalletId) =>
	set(
		web3AccountsAtom,
		get(web3AccountsAtom).filter((account) => account.id !== id)
	)
);

export const removeAllWeb3AccountsAtom = atom(null, (get, set) => set(web3AccountsAtom, []));

export const updateWeb3AccountAtom = atom(
	null,
	(get, set, accountToUpdateId: TWeb3Account['id'], updatedAccountProperties: Partial<Omit<TWeb3Account, 'id'>>) =>
		set(web3AccountsAtom, updateWeb3Account(get(web3AccountsAtom), accountToUpdateId, updatedAccountProperties))
);

export const chainIdAtom = atomFamily((type: EWalletType) =>
	selectAtom(web3AccountsAtom, (get) => {
		const account = get.find((acc) => acc.type === type);

		if (!account) return undefined;

		if (isTestEnv() && account.type === EWalletType.ethereum) return NETWORKS_DATA.sepolia.chainId;

		// ensure account.chainId is hex value
		if (typeof account.chainId === 'number') return '0x' + account.chainId.toString(16);

		return account.chainId;
	})
);

export const walletIdAtom = atomFamily((type: EWalletType) =>
	selectAtom(web3AccountsAtom, (get) => {
		const account = get.find((acc) => acc.type === type);

		if (!account) return undefined;

		return account.id;
	})
);

const isValidNetworkAtomCreator = (type: EWalletType) =>
	atom((get) => {
		const chainId = get(chainIdAtom(type));

		if (!chainId) return false;

		return VALID_CHAIN_IDS.includes(chainId);
	});

const addressAtomCreator = (type: EWalletType) =>
	atom((get) => {
		const account = get(web3AccountsAtom).find((acc) => acc.type === type);

		if (!account) return undefined;
		try {
			switch (account.type) {
				case EWalletType.ethereum: {
					const {
						web3Provider,
						web3Instance,
						isDevWallet: isWallet
					} = account.provider as TProviderMap[EWalletType.ethereum];
					const name = account.name;

					if ((!web3Provider && !isWallet) || !name || !web3Instance) return undefined;

					let newAddress: Address | undefined;

					switch (name) {
						case WALLETS.blocknative.name:
							newAddress = Address.from(web3Instance.address);
							break;
						default:
							break;
					}

					if (!newAddress) return undefined;

					return newAddress;
				}
				case EWalletType.starknet: {
					const { web3Instance } = account.provider as TProviderMap[EWalletType.starknet];
					const walletId = get(walletIdAtom(type));

					if (!web3Instance || !walletId) return undefined;

					return Address.from(web3Instance.selectedAddress);
				}
				default:
					return undefined;
			}
		} catch (err) {
			console.error('addressAtomCreator', err);
		}

		return undefined;
	});

export const providerAtom = atomFamily((type: EWalletType) =>
	atom((get) => {
		const web3Accounts = get(web3AccountsAtom);
		const account = web3Accounts.find((acc) => acc.type === type);

		if (!account) return undefined;

		const isConnected = get(isWalletConnectedAtom(type));

		return isConnected ? account.provider : undefined;
	})
);

export const addressAtom = atomFamily((type: EWalletType) => addressAtomCreator(type));

export const isValidNetworkAtom = atomFamily((type: EWalletType) => isValidNetworkAtomCreator(type));

export const isWalletConnectedAtom = atomFamily((type: EWalletType) => atom((get) => !!get(addressAtom(type))));

export const connectedWeb3AccountAtom = atomFamily((type: EWalletType) =>
	atom((get) => {
		const web3Accounts = get(web3AccountsAtom);
		const account = web3Accounts.find((acc) => acc.type === type);

		if (!account) return undefined;

		const isConnected = get(isWalletConnectedAtom(type));

		return isConnected ? account : undefined;
	})
);

export const connectedWeb3AccountsIdAtom = atom((get) => {
	const web3Accounts = get(web3AccountsAtom);
	const connectedWeb3Accounts = web3Accounts.filter((web3Account) => get(isWalletConnectedAtom(web3Account.type)));
	return connectedWeb3Accounts.map((connectedWeb3Account) => connectedWeb3Account.id);
});

export const connectedWeb3AccountsTypeAtom = atom((get) => {
	const web3Accounts = get(web3AccountsAtom);

	return web3Accounts.map((acc) => acc.type);
});
